import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Loader2, MailCheck, Lock, Share2, Home, LogOut, RotateCcw, GraduationCap, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { getUser, clearAuth, authFetch } from "@/lib/auth";
import AuthModal from "@/components/AuthModal";
import { PERSONA_META, DOMAIN_ORDER, useLang, LangToggle } from "@/components/landing/i18n";

const slide = { initial: { opacity: 0, x: 36 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -36 }, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } };

const TRACKS = {
  student: { Icon: GraduationCap, color: "#1F5BFF", title: "Student", sub: "Grade 8 to postgraduate — stream, subject and career-direction mapping" },
  professional: { Icon: Briefcase, color: "#7C3AED", title: "Career Professional", sub: "Working professionals — role fit, leadership style and pivot mapping" },
};

const INDUSTRIES = ["Technology & IT", "Finance & Banking", "Healthcare & Pharma", "Marketing & Media", "Education & Research", "Design & Creative"];

const INDUSTRY_HI = { "Technology & IT": "टेक्नोलॉजी और IT", "Finance & Banking": "फाइनेंस और बैंकिंग", "Healthcare & Pharma": "हेल्थकेयर और फार्मा", "Marketing & Media": "मार्केटिंग और मीडिया", "Education & Research": "शिक्षा और शोध", "Design & Creative": "डिज़ाइन और क्रिएटिव" };
const SUB_HI = { logical: "तार्किक", numerical: "संख्यात्मक", verbal: "भाषाई", spatial: "स्थानिक" };

const UI = {
  en: {
    title: "Full Assessment", sub: "Two stages · about 10 minutes · progress auto-saves", choose: "Choose your path",
    continue: "Continue", industry: "Your industry", industryHint: "So role recommendations speak your language.",
    careerTrack: "Career track", aptStage: "Stage 1 · Aptitude", prefStage: "Stage 2 · Your preferences",
    question: "Question", aptitude: "Aptitude", yourCall: "Your call", doneTitle: "All done!",
    doneCopy: "Your full report has been generated and emailed to you. You can download the detailed PDF from your dashboard.",
    sent: "Report emailed", dashboard: "View dashboard", error: "Something went wrong", retry: "Retry",
    gateTitle: "The full battery is for members", gateCopy: "Separate batteries for students and career professionals — 24 adaptive questions, a scored report emailed to you, and a shareable result card. Free during early access.", signin: "Sign in / Create free account",
    chooseBattery: "Choose your battery", chooseBatterySub: "Two separate tests, two separate reports — pick the one built for where you are.", industrySub: "Your report's role clusters will be tailored to this industry.", suggested: "Suggested for you", back: "← Back to batteries",
    workStyle: "Work style & interests", personality: "Personality & interests", level: "level", result: "Your result", aptitudeScore: "Aptitude score", emailSent: "Full {track} report sent to {email} — check your inbox.", emailPending: "Report saved to your account. Email delivery is pending.", share: "View & share your card", reports: "My reports", retake: "Retake", tryBattery: "Try the {track} battery", home: "Home", signout: "Sign out",
  },
  hi: {
    title: "पूरा आकलन", sub: "दो चरण · लगभग 10 मिनट · प्रगति अपने-आप सेव होती है", choose: "अपना रास्ता चुनें",
    continue: "आगे बढ़ें", industry: "आपका उद्योग", industryHint: "ताकि भूमिका-सुझाव आपकी भाषा में हों।",
    careerTrack: "करियर ट्रैक", aptStage: "चरण 1 · योग्यता", prefStage: "चरण 2 · आपकी पसंद",
    question: "प्रश्न", aptitude: "योग्यता", yourCall: "आपका फ़ैसला", doneTitle: "हो गया!",
    doneCopy: "आपकी पूरी रिपोर्ट तैयार होकर ईमेल हो गई है। विस्तृत PDF आपके डैशबोर्ड से डाउनलोड करें।",
    sent: "रिपोर्ट ईमेल हुई", dashboard: "डैशबोर्ड देखें", error: "कुछ गड़बड़ हुई", retry: "फिर कोशिश करें",
    gateTitle: "पूरी बैटरी सदस्यों के लिए है", gateCopy: "विद्यार्थियों और प्रोफेशनल्स के लिए अलग बैटरी — 24 अनुकूल प्रश्न, स्कोर की गई रिपोर्ट ईमेल पर, और साझा करने योग्य परिणाम कार्ड। अर्ली एक्सेस में मुफ़्त।", signin: "साइन इन / मुफ़्त खाता बनाएँ",
    chooseBattery: "अपनी बैटरी चुनें", chooseBatterySub: "दो अलग टेस्ट, दो अलग रिपोर्ट — अपनी स्थिति के अनुसार चुनें।", industrySub: "आपकी रिपोर्ट की भूमिका-सूचियाँ इसी उद्योग के अनुसार होंगी।", suggested: "आपके लिए सुझाया गया", back: "← बैटरी पर वापस",
    workStyle: "कार्य-शैली और रुचियाँ", personality: "व्यक्तित्व और रुचियाँ", level: "स्तर", result: "आपका परिणाम", aptitudeScore: "योग्यता स्कोर", emailSent: "पूरी {track} रिपोर्ट {email} पर भेज दी गई है — अपना इनबॉक्स देखें।", emailPending: "रिपोर्ट आपके खाते में सेव है। ईमेल डिलीवरी लंबित है।", share: "कार्ड देखें और साझा करें", reports: "मेरी रिपोर्ट", retake: "फिर से करें", tryBattery: "{track} बैटरी आज़माएँ", home: "होम", signout: "साइन आउट",
  },
};

export default function FullAssessment() {
  const { lang } = useLang();
  const ui = UI[lang];
  const [user, setUser] = useState(getUser());
  const [authOpen, setAuthOpen] = useState(false);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [industryStep, setIndustryStep] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const res = await authFetch("/api/full/state");
      if (res.status === 401) {
        clearAuth();
        setUser(null);
        return;
      }
      setState(await res.json());
    })();
  }, [user]);

  useEffect(() => {
    if (!state || state.done || !state.question) return;
    if (!localStorage.getItem("biq_full_start")) localStorage.setItem("biq_full_start", String(Date.now()));
    const iv = setInterval(() => {
      const start = Number(localStorage.getItem("biq_full_start") || Date.now());
      setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    }, 1000);
    return () => clearInterval(iv);
  }, [state]);

  const startTrack = async (track, industry = null) => {
    setBusy(true);
    try {
      const res = await authFetch("/api/full/start", { method: "POST", body: JSON.stringify({ track, industry, lang }) });
      const d = await res.json();
      if (!res.ok) toast.error(typeof d.detail === "string" ? d.detail : "Something went wrong");
      else {
        localStorage.setItem("biq_full_start", String(Date.now()));
        setElapsed(0);
        setState(d);
        setIndustryStep(false);
        if (d.teaserIncluded) toast.success(lang === "hi" ? "आपके लैंडिंग क्विज़ के जवाब इस बैटरी में शामिल किए गए हैं" : "Your quick-check answers from the landing quiz are included in this battery");
      }
    } catch {
      toast.error("Network error. Please try again.");
    }
    setBusy(false);
  };

  const retake = async (track) => {
    setBusy(true);
    try {
      const res = await authFetch(`/api/full/retake${track ? `?track=${track}` : ""}`, { method: "POST" });
      const d = await res.json();
      localStorage.setItem("biq_full_start", String(Date.now()));
      setElapsed(0);
      setState(d);
      if (d.teaserIncluded) toast.success(lang === "hi" ? "आपके लैंडिंग क्विज़ के जवाब इस बैटरी में शामिल किए गए हैं" : "Your quick-check answers from the landing quiz are included in this battery");
    } catch {
      toast.error("Network error");
    }
    setBusy(false);
  };

  const answer = async (i) => {
    if (busy || !state?.question) return;
    setBusy(true);
    try {
      const res = await authFetch("/api/full/answer", { method: "POST", body: JSON.stringify({ qid: state.question.qid, choice: i }) });
      const d = await res.json();
      if (!res.ok) toast.error(typeof d.detail === "string" ? d.detail : "Something went wrong");
      else {
        setState(d);
        if (d.done) {
          localStorage.removeItem("biq_full_start");
          toast.success("Assessment complete — your report is ready!");
        }
      }
    } catch {
      toast.error("Network error. Please try again.");
    }
    setBusy(false);
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const q = state?.question;
  const result = state?.done ? state.result : null;
  const persona = result ? PERSONA_META[result.persona] : null;
  const ptxt = persona ? persona[lang] || persona.en : null;
  const trackLabel = (tr) => (tr === "professional" ? (lang === "hi" ? "करियर प्रोफेशनल" : "Career Professional") : (lang === "hi" ? "विद्यार्थी" : "Student"));
  const curTrack = state?.track || result?.track || "student";

  return (
    <div className="min-h-screen bg-paper text-ink font-body" data-testid="full-page">
      <header className="max-w-3xl mx-auto px-5 pt-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="full-home-logo">
          <img src="/brightiq-logo.png" alt="Bright IQ" className="h-10 w-10 rounded-lg object-contain" />
          <span className="font-display font-bold text-lg tracking-tight">Bright<span className="text-iqblue">IQ</span></span>
        </Link>
        <div className="flex items-center gap-3">
          <LangToggle />
          {q && <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-ink/45" data-testid="full-track-chip">{trackLabel(curTrack)}</span>}
          {q && (
            <div className="flex items-center gap-2 text-sm font-semibold text-ink/60" data-testid="full-timer">
              <Clock className="h-4 w-4" /> {mm}:{ss}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-14">
        {!user && (
          <motion.div {...slide} className="rounded-[2rem] bg-cream border border-ink/10 p-10 text-center" data-testid="full-gate">
            <span className="inline-flex h-14 w-14 rounded-2xl bg-iqblue/10 items-center justify-center">
              <Lock className="h-6 w-6 text-iqblue" />
            </span>
            <h1 className="font-display font-bold text-3xl tracking-tight mt-6">{ui.gateTitle}</h1>
            <p className="mt-3 text-ink/60 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
              {ui.gateCopy}
            </p>
            <button onClick={() => setAuthOpen(true)} data-testid="full-signin-button" className="mt-8 bg-ink text-paper font-semibold px-8 py-4 rounded-full hover:bg-iqblue transition-colors duration-300">
              {ui.signin}
            </button>
          </motion.div>
        )}

        {user && !state && (
          <div className="py-24 flex justify-center" data-testid="full-loading">
            <Loader2 className="h-8 w-8 animate-spin text-iqblue" />
          </div>
        )}

        {user && state && state.needsTrack && (
          <motion.div {...slide} data-testid="track-picker">
            <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-center">{industryStep ? ui.industry : ui.chooseBattery}</h1>
            <p className="mt-3 text-ink/60 text-sm sm:text-base text-center max-w-md mx-auto">
              {industryStep ? ui.industrySub : ui.chooseBatterySub}
            </p>
            {!industryStep && (
              <div className="mt-10 grid sm:grid-cols-2 gap-5">
                {Object.entries(TRACKS).map(([key, tr]) => (
                  <button
                    key={key}
                    onClick={() => (key === "professional" ? setIndustryStep(true) : startTrack(key))}
                    disabled={busy}
                    data-testid={`track-${key}`}
                    className={`group relative rounded-3xl border-2 bg-cream p-8 text-left transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-20px_rgba(10,22,51,0.2)] disabled:opacity-60 ${state.suggested === key ? "border-iqblue" : "border-ink/10 hover:border-ink/25"}`}
                  >
                    {state.suggested === key && (
                      <span className="absolute -top-3 left-6 bg-iqblue text-paper text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1 rounded-full">{ui.suggested}</span>
                    )}
                    <span className="inline-flex h-12 w-12 rounded-2xl items-center justify-center transition-transform duration-500 group-hover:scale-110" style={{ background: `${tr.color}18` }}>
                      <tr.Icon className="h-5 w-5" style={{ color: tr.color }} />
                    </span>
                    <div className="font-display font-bold text-xl tracking-tight mt-5">{tr.title}</div>
                    <p className="mt-2 text-sm text-ink/60 leading-relaxed">{tr.sub}</p>
                  </button>
                ))}
              </div>
            )}
            {industryStep && (
              <div data-testid="industry-picker">
                <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {INDUSTRIES.map((ind) => (
                    <button
                      key={ind}
                      onClick={() => startTrack("professional", ind)}
                      disabled={busy}
                      data-testid={`industry-${ind.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="group rounded-3xl border-2 border-ink/10 bg-cream p-6 text-left transition-all duration-500 hover:-translate-y-1 hover:border-iqpurple hover:shadow-[0_25px_50px_-20px_rgba(10,22,51,0.2)] disabled:opacity-60"
                    >
                      <span className="font-display font-bold text-base tracking-tight group-hover:text-iqpurple transition-colors">{lang === "hi" ? INDUSTRY_HI[ind] : ind}</span>
                    </button>
                  ))}
                </div>
                <button onClick={() => setIndustryStep(false)} data-testid="industry-back-button" className="mt-6 text-xs font-semibold text-ink/45 hover:text-ink transition-colors">
                  {ui.back}
                </button>
              </div>
            )}
          </motion.div>
        )}

        {user && state && !state.done && q && (
          <>
            <div className="flex items-center justify-between text-xs font-semibold tracking-[0.2em] uppercase text-ink/45 mb-3">
              <span>{q.part === "apt" ? `${ui.aptitude} · ${lang === "hi" ? SUB_HI[q.sub] || q.sub : q.sub} · ${ui.level} ${q.diff}` : curTrack === "professional" ? ui.workStyle : ui.personality}</span>
              <span>{q.n} / {q.total}</span>
            </div>
            <div className="h-1.5 rounded-full bg-ink/8 overflow-hidden mb-10">
              <motion.div data-testid="full-progress-bar" className="h-full bg-gradient-to-r from-iqblue via-iqpurple to-iqpink" animate={{ width: `${(q.n / q.total) * 100}%` }} transition={{ duration: 0.5 }} />
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={q.qid} {...slide}>
                <h1 data-testid="full-question-text" className="font-display font-bold text-2xl sm:text-3xl tracking-tight leading-snug">{q.q}</h1>
                <div className="mt-8 space-y-3">
                  {q.opts.map((opt, i) => (
                    <button
                      key={i}
                      data-testid={`full-option-${i}`}
                      disabled={busy}
                      onClick={() => answer(i)}
                      className="w-full text-left rounded-2xl border border-ink/12 bg-cream px-6 py-4 text-sm sm:text-base font-medium hover:border-iqblue hover:bg-white hover:shadow-md active:scale-[0.99] disabled:opacity-50 transition-all duration-200"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {busy && <div className="mt-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-iqblue" /></div>}
              </motion.div>
            </AnimatePresence>
          </>
        )}

        {user && result && persona && (
          <motion.div {...slide} className="rounded-[2rem] bg-cream border border-ink/10 p-8 sm:p-12 text-center" data-testid="full-result">
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-ink/40">{trackLabel(result.track)} battery · {ui.result}</div>
            <h1 data-testid="full-persona" className="font-display font-bold text-4xl sm:text-5xl tracking-tight mt-3" style={{ color: persona.color }}>
              {ptxt.name}
            </h1>
            <p className="mt-4 text-ink/65 max-w-md mx-auto leading-relaxed">{ptxt.blurb}</p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink text-paper px-5 py-2 text-sm font-semibold" data-testid="full-apt-score">
              {ui.aptitudeScore}: {result.aptPct}%
            </div>
            <div className="mt-8 space-y-2.5 text-left max-w-md mx-auto">
              {DOMAIN_ORDER.map((key) => (
                <div key={key} className="flex items-center gap-3">
                  <span className="w-28 text-[11px] font-semibold text-ink/55 text-right truncate">{PERSONA_META[key][lang]?.name || PERSONA_META[key].en.name}</span>
                  <div className="flex-1 h-2 rounded-full bg-ink/8 overflow-hidden">
                    <motion.div className="h-full rounded-full" style={{ background: PERSONA_META[key].color }} initial={{ width: 0 }} animate={{ width: `${((result.scores[key] || 0) / 12) * 100}%` }} transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }} />
                  </div>
                  <span className="w-6 text-xs font-bold" style={{ color: PERSONA_META[key].color }}>{result.scores[key] || 0}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-start gap-2.5 rounded-2xl bg-iqgreen/10 border border-iqgreen/25 px-4 py-3.5 text-left max-w-md mx-auto">
              <MailCheck className="h-4 w-4 text-iqgreen shrink-0 mt-0.5" />
              <p className="text-xs text-ink/65 leading-relaxed" data-testid="full-email-note">
                {result.emailSent ? ui.emailSent.replace("{track}", trackLabel(result.track).toLowerCase()).replace("{email}", user.email) : ui.emailPending}
              </p>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
              <Link to={`/r/${result.reportId}`} data-testid="full-share-link" className="flex items-center justify-center gap-2 bg-ink text-paper font-semibold px-7 py-3.5 rounded-full hover:bg-iqblue transition-colors duration-300">
                <Share2 className="h-4 w-4" /> {ui.share}
              </Link>
              <Link to="/reports" data-testid="full-reports-link" className="flex items-center justify-center gap-2 border border-ink/20 font-semibold px-7 py-3.5 rounded-full hover:bg-ink hover:text-paper transition-colors duration-300">
                {ui.reports}
              </Link>
              <button data-testid="full-retake-button" onClick={() => retake()} disabled={busy} className="flex items-center justify-center gap-2 border border-ink/20 font-semibold px-7 py-3.5 rounded-full hover:bg-ink hover:text-paper disabled:opacity-60 transition-colors duration-300">
                <RotateCcw className="h-4 w-4" /> {ui.retake}
              </button>
              <button data-testid="full-switch-track-button" onClick={() => retake(result.track === "professional" ? "student" : "professional")} disabled={busy} className="flex items-center justify-center gap-2 border border-iqpurple/30 text-iqpurple font-semibold px-7 py-3.5 rounded-full hover:bg-iqpurple hover:text-paper disabled:opacity-60 transition-colors duration-300">
                {result.track === "professional" ? <GraduationCap className="h-4 w-4" /> : <Briefcase className="h-4 w-4" />}
                {ui.tryBattery.replace("{track}", result.track === "professional" ? (lang === "hi" ? "विद्यार्थी" : "Student") : (lang === "hi" ? "प्रोफेशनल" : "Professional"))}
              </button>
              <Link to="/" data-testid="full-home-link" className="flex items-center justify-center gap-2 border border-ink/20 font-semibold px-7 py-3.5 rounded-full hover:bg-ink hover:text-paper transition-colors duration-300">
                <Home className="h-4 w-4" /> {ui.home}
              </Link>
            </div>
            <button onClick={() => { clearAuth(); setUser(null); setState(null); }} data-testid="full-logout-button" className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-ink/45 hover:text-iqpink transition-colors">
              <LogOut className="h-3.5 w-3.5" /> {ui.signout}
            </button>
          </motion.div>
        )}
      </main>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onSuccess={(u) => setUser(u)} />
    </div>
  );
}
